import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, GripVertical, Users, Search, X } from "lucide-react";
import { toast } from "sonner";

const COLORS = {
  yellow: { bg: "#FEF3C7", border: "#FCD34D", dot: "#FBBF24" },
  pink: { bg: "#FCE7F3", border: "#F9A8D4", dot: "#EC4899" },
  blue: { bg: "#DBEAFE", border: "#93C5FD", dot: "#3B82F6" },
  green: { bg: "#D1FAE5", border: "#6EE7B7", dot: "#10B981" },
  orange: { bg: "#FFEDD5", border: "#FDBA74", dot: "#F97316" },
  purple: { bg: "#EDE9FE", border: "#C4B5FD", dot: "#8B5CF6" },
};
const COLOR_KEYS = Object.keys(COLORS);
const NOTE_W = 220;
const NOTE_H = 210;

export default function Notebook() {
  const [notes, setNotes] = useState([]);
  const [aggregate, setAggregate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const boardRef = useRef(null);
  const dragRef = useRef(null);
  const notesRef = useRef([]);
  useEffect(() => { notesRef.current = notes; }, [notes]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notebook/notes");
      setNotes(data.notes);
      setAggregate(data.aggregate);
    } catch (e) {
      if (!e?.__silent) toast.error("Failed to load notebook");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addNote = async () => {
    const scroll = boardRef.current;
    const baseX = 40 + (scroll ? scroll.scrollLeft : 0) + Math.random() * 40;
    const baseY = 40 + (scroll ? scroll.scrollTop : 0) + Math.random() * 40;
    const colour = COLOR_KEYS[Math.floor(Math.random() * COLOR_KEYS.length)];
    try {
      const { data } = await api.post("/notebook/notes", { text: "", color: colour, x: baseX, y: baseY });
      setNotes((prev) => [...prev, data]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add note");
    }
  };

  const addNoteToClient = async (orgId) => {
    const colour = COLOR_KEYS[Math.floor(Math.random() * COLOR_KEYS.length)];
    try {
      const { data } = await api.post("/notebook/notes", { text: "", color: colour, organization_id: orgId });
      setNotes((prev) => [...prev, data]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add note");
    }
  };

  const persistText = async (note, text) => {
    try { await api.put(`/notebook/notes/${note.id}`, { text, color: note.color, x: note.x, y: note.y }); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save note"); }
  };

  const setColor = async (note, color) => {
    setNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, color } : n)));
    try { await api.put(`/notebook/notes/${note.id}`, { text: note.text, color, x: note.x, y: note.y }); }
    catch (e) { toast.error("Couldn't update colour"); }
  };

  const removeNote = async (note) => {
    setNotes((prev) => prev.filter((n) => n.id !== note.id));
    try { await api.delete(`/notebook/notes/${note.id}`); }
    catch (e) { toast.error("Couldn't delete note"); load(); }
  };

  const onTextChange = (id, text) =>
    setNotes((prev) => prev.map((n) => (n.id === id ? { ...n, text } : n)));

  // ---- Drag handling ----
  const onDragMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    const nx = Math.max(0, d.origX + dx);
    const ny = Math.max(0, d.origY + dy);
    setNotes((prev) => prev.map((n) => (n.id === d.id ? { ...n, x: nx, y: ny } : n)));
  }, []);

  const onDragEnd = useCallback(async () => {
    const d = dragRef.current;
    dragRef.current = null;
    window.removeEventListener("pointermove", onDragMove);
    window.removeEventListener("pointerup", onDragEnd);
    if (!d) return;
    const note = notesRef.current.find((n) => n.id === d.id) || null;
    if (note) {
      try { await api.patch(`/notebook/notes/${note.id}/position`, { x: note.x, y: note.y }); }
      catch { /* silent */ }
    }
  }, [onDragMove]);

  const startDrag = (e, note) => {
    e.preventDefault();
    dragRef.current = {
      id: note.id,
      startX: e.clientX,
      startY: e.clientY,
      origX: note.x,
      origY: note.y,
    };
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", onDragEnd);
  };

  useEffect(() => () => {
    window.removeEventListener("pointermove", onDragMove);
    window.removeEventListener("pointerup", onDragEnd);
  }, [onDragMove, onDragEnd]);

  const clientNames = Array.from(
    new Set(notes.map((n) => n.organization_name).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const q = search.trim().toLowerCase();
  const visibleNotes = notes.filter((n) => {
    if (clientFilter && n.organization_name !== clientFilter) return false;
    if (q && !(n.text || "").toLowerCase().includes(q)) return false;
    return true;
  });

  // Aggregate view: group visible notes into a column per client.
  const byClient = visibleNotes.reduce((acc, n) => {
    const cn = n.organization_name || "Unknown";
    (acc[cn] = acc[cn] || []).push(n);
    return acc;
  }, {});
  const columnClients = Object.keys(byClient).sort((a, b) => a.localeCompare(b));

  return (
    <div data-testid="notebook-page" className="flex flex-col h-[calc(100vh-0px)] lg:h-screen">
      <PageHeader
        eyebrow="[11] Notebook"
        title="Notebook"
        subtitle={aggregate
          ? "All clients' notes, organised in a column per client. Click a note to edit."
          : "A whiteboard of sticky notes for this client. Drag to arrange, click a note to edit."}
        actions={
          <Button
            data-testid="add-note-btn"
            onClick={addNote}
            className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase"
          >
            <Plus className="w-3.5 h-3.5 mr-2" /> Add Note
          </Button>
        }
      />

      {aggregate && (
        <div className="px-4 sm:px-6 lg:px-8 pt-3">
          <div data-testid="notebook-aggregate-banner" className="inline-flex items-center gap-2 border border-[#E5E0D5] bg-[#F0EDE3] px-3 py-1.5">
            <Users className="w-3.5 h-3.5 text-[#5A5A5A]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#5A5A5A]">Viewing all clients · notes are tagged by client</span>
          </div>
        </div>
      )}

      {!loading && notes.length > 0 && (
        <div className="px-4 sm:px-6 lg:px-8 pt-3 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-[#9A9A9A] absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="notebook-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search note text…"
              className="rounded-none bg-white border-[#E5E0D5] font-mono text-xs h-9 pl-8 w-56"
            />
          </div>
          {aggregate && clientNames.length > 0 && (
            <select
              data-testid="notebook-client-filter"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              className="rounded-none bg-white border border-[#E5E0D5] font-mono text-xs h-9 px-2 text-[#3A3A3A] focus:outline-none focus:ring-1 focus:ring-[#F37B2A]"
            >
              <option value="">All clients ({notes.length})</option>
              {clientNames.map((cn) => (
                <option key={cn} value={cn}>{cn} ({notes.filter((n) => n.organization_name === cn).length})</option>
              ))}
            </select>
          )}
          {(search || clientFilter) && (
            <button
              data-testid="notebook-clear-filters"
              onClick={() => { setSearch(""); setClientFilter(""); }}
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[#7A7A7A] hover:text-[#B91C1C]"
            >
              <X className="w-3 h-3" /> Clear
            </button>
          )}
          <span data-testid="notebook-filter-count" className="font-mono text-[10px] uppercase tracking-wider text-[#9A9A9A] ml-auto">
            Showing {visibleNotes.length} of {notes.length}
          </span>
        </div>
      )}

      <div className="flex-1 min-h-0 p-4 sm:p-6 lg:p-8 pt-4">
        {loading ? (
          <div className="border border-[#E5E0D5] bg-[#FBFAF6] w-full h-full min-h-[70vh] flex items-center justify-center font-mono text-xs uppercase tracking-wider text-[#9A9A9A]">Loading…</div>
        ) : notes.length === 0 ? (
          <div className="border border-[#E5E0D5] bg-[#FBFAF6] w-full h-full min-h-[70vh] flex flex-col items-center justify-center gap-3 text-center px-6">
            <div className="font-mono text-xs text-[#9A9A9A]">No notes yet.</div>
            <button onClick={addNote} data-testid="empty-add-note-btn" className="font-mono text-xs uppercase tracking-wider text-[#F37B2A] hover:text-[#D86A1F]">+ Add your first sticky note</button>
          </div>
        ) : visibleNotes.length === 0 ? (
          <div className="border border-[#E5E0D5] bg-[#FBFAF6] w-full h-full min-h-[70vh] flex items-center justify-center px-6" data-testid="notebook-no-matches">
            <div className="font-mono text-xs text-[#9A9A9A]">No notes match your filters.</div>
          </div>
        ) : aggregate ? (
          /* Aggregated view: one column per client, notes stacked below */
          <div data-testid="notebook-columns" className="flex gap-4 overflow-x-auto h-full min-h-[70vh] pb-2 items-start">
            {columnClients.map((cn) => (
              <div key={cn} data-testid={`notebook-column-${cn}`} className="w-72 shrink-0 flex flex-col">
                <div className="flex items-center gap-2 px-2 py-2 mb-2 border-b-2 border-[#F37B2A] bg-[#F0EDE3]">
                  <span className="font-mono text-xs tracking-[0.15em] uppercase text-[#1A1A1A] truncate" title={cn}>{cn}</span>
                  <span className="font-mono text-[10px] text-[#9A9A9A]">{byClient[cn].length}</span>
                  <button
                    data-testid={`column-add-note-${cn}`}
                    onClick={() => addNoteToClient(byClient[cn][0].organization_id)}
                    className="ml-auto inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-[#F37B2A] hover:text-[#D86A1F]"
                    title={`Add a note for ${cn}`}
                  >
                    <Plus className="w-3 h-3" /> Note
                  </button>
                </div>
                <div className="space-y-3">
                  {byClient[cn].map((note) => {
                    const c = COLORS[note.color] || COLORS.yellow;
                    return (
                      <div key={note.id} data-testid={`note-${note.id}`} className="shadow-[2px_3px_8px_rgba(0,0,0,0.08)] flex flex-col" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
                        <div className="flex items-center justify-end px-2 py-1" style={{ borderBottom: `1px solid ${c.border}` }}>
                          <button data-testid={`note-delete-${note.id}`} onClick={() => removeNote(note)} className="p-0.5 text-black/40 hover:text-[#B91C1C]" title="Delete note"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                        <textarea
                          data-testid={`note-text-${note.id}`}
                          value={note.text}
                          onChange={(e) => onTextChange(note.id, e.target.value)}
                          onBlur={(e) => persistText(note, e.target.value)}
                          placeholder="Type a note…"
                          rows={4}
                          className="w-full resize-none bg-transparent border-0 outline-none px-3 py-2 text-sm text-[#1A1A1A] placeholder:text-black/30 font-medium leading-snug"
                        />
                        <div className="flex items-center gap-1.5 px-2 py-1.5" style={{ borderTop: `1px solid ${c.border}` }}>
                          {COLOR_KEYS.map((k) => (
                            <button
                              key={k}
                              data-testid={`note-color-${k}-${note.id}`}
                              onClick={() => setColor(note, k)}
                              className={`w-4 h-4 rounded-full transition-transform hover:scale-110 ${note.color === k ? "ring-2 ring-offset-1 ring-black/40" : ""}`}
                              style={{ background: COLORS[k].dot }}
                              title={k}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Single-client view: draggable whiteboard */
          <div
            ref={boardRef}
            data-testid="notebook-board"
            className="relative w-full h-full min-h-[70vh] border border-[#E5E0D5] bg-[#FBFAF6] overflow-auto"
            style={{
              backgroundImage: "radial-gradient(#E5E0D5 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          >
            {/* canvas spacer to allow scrolling as notes spread out */}
            <div style={{ width: 1, height: 1, ...spread(visibleNotes) }} />

            {visibleNotes.map((note) => {
              const c = COLORS[note.color] || COLORS.yellow;
              return (
                <div
                  key={note.id}
                  data-testid={`note-${note.id}`}
                  className="absolute shadow-[3px_4px_10px_rgba(0,0,0,0.10)] flex flex-col"
                  style={{ left: note.x, top: note.y, width: NOTE_W, minHeight: NOTE_H, background: c.bg, border: `1px solid ${c.border}` }}
                >
                  {/* drag handle */}
                  <div
                    data-testid={`note-drag-${note.id}`}
                    onPointerDown={(e) => startDrag(e, note)}
                    className="flex items-center justify-between px-2 py-1.5 cursor-grab active:cursor-grabbing touch-none"
                    style={{ borderBottom: `1px solid ${c.border}` }}
                  >
                    <GripVertical className="w-3.5 h-3.5 text-black/30 shrink-0" />
                    <button
                      data-testid={`note-delete-${note.id}`}
                      onClick={() => removeNote(note)}
                      className="p-0.5 text-black/40 hover:text-[#B91C1C] shrink-0"
                      title="Delete note"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <textarea
                    data-testid={`note-text-${note.id}`}
                    value={note.text}
                    onChange={(e) => onTextChange(note.id, e.target.value)}
                    onBlur={(e) => persistText(note, e.target.value)}
                    placeholder="Type a note…"
                    className="flex-1 w-full resize-none bg-transparent border-0 outline-none px-3 py-2 text-sm text-[#1A1A1A] placeholder:text-black/30 font-medium leading-snug"
                    style={{ minHeight: 120 }}
                  />

                  {/* colour picker */}
                  <div className="flex items-center gap-1.5 px-2 py-1.5" style={{ borderTop: `1px solid ${c.border}` }}>
                    {COLOR_KEYS.map((k) => (
                      <button
                        key={k}
                        data-testid={`note-color-${k}-${note.id}`}
                        onClick={() => setColor(note, k)}
                        className={`w-4 h-4 rounded-full transition-transform hover:scale-110 ${note.color === k ? "ring-2 ring-offset-1 ring-black/40" : ""}`}
                        style={{ background: COLORS[k].dot }}
                        title={k}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Ensures the scroll canvas grows to contain the furthest note.
function spread(notes) {
  let maxX = 0, maxY = 0;
  for (const n of notes) {
    maxX = Math.max(maxX, (n.x || 0) + NOTE_W + 60);
    maxY = Math.max(maxY, (n.y || 0) + NOTE_H + 60);
  }
  return { position: "absolute", left: maxX, top: maxY };
}
